import {
	AfterViewInit,
	Directive,
	ElementRef,
	Input,
	OnDestroy,
	Renderer2,
} from '@angular/core';

@Directive({
	selector: '[cassStickyTableHeader]',
})
export class StickyTableHeaderDirective implements AfterViewInit, OnDestroy {
	@Input() parentContainer: HTMLElement | null = null;
	@Input() styleClass!: string;

	private isSticky = false;
	private scrollListener: (() => void) | undefined;
	private windowScrollListener: (() => void) | undefined;
	private mutationObserver: MutationObserver | null = null;
	private resizeObserver: ResizeObserver | undefined = undefined;
	private table: HTMLElement | null = null;
	private headerRow: HTMLElement | null = null;
	private tableContainer: HTMLElement | null = null;
	private placeholderHeaderRow: HTMLElement | null = null;

	constructor(
		private el: ElementRef,
		private renderer: Renderer2) {
	}

	ngAfterViewInit() {
		this.tableContainer = this.el.nativeElement;
		this.table = this.el.nativeElement.querySelector('table');
		this.headerRow = this.el.nativeElement.querySelector('thead');

		if (!this.table || !this.headerRow) {
			console.warn('Table or header not found');
			return;
		}

		const parentContainer = this.parentContainer || window;

		// listen scroll event on parent container / window
		this.windowScrollListener = this.renderer.listen(parentContainer, 'scroll', () => {
			this.onVerticalScroll();
		});

		// listen scroll event on table container
		this.scrollListener = this.renderer.listen(this.tableContainer, 'scroll', () => {
			this.onHorizontalScroll();
		});

		this.initializeTableResizeObserver();
		this.checkTableVisibility();
	}

	ngOnDestroy() {
		if (this.scrollListener) {
			this.scrollListener();
		}
		if (this.windowScrollListener) {
			this.windowScrollListener();
		}
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
		}
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
		if (this.table && this.resizeObserver) {
			this.resizeObserver.unobserve(this.table);
		} // unobserve if table is destroyed
		if (this.placeholderHeaderRow) {
			this.placeholderHeaderRow.remove();
		}
	}

	// Synchronise the table Header and table Body column widths
	private syncHeaderAndBodyColumnWidths(): void {
		if (!this.headerRow) {
			return;
		}
		const firstDataRow = this.tableContainer?.querySelector('tbody tr') as HTMLElement;
		if (!firstDataRow) {
			return;
		}

		const headerCells = this.headerRow.querySelectorAll('th');
		const dataCells = firstDataRow.querySelectorAll('td');

		if (headerCells.length !== dataCells.length) {
			return;
		}

		// Calculates the maximum width between the header and data cell at the same index.
		headerCells.forEach((headerCell: HTMLElement, index: number) => {
			const dataCell = dataCells[index] as HTMLElement;
			const maxWidth = Math.max(headerCell.offsetWidth, dataCell.offsetWidth);

			headerCell.style.width = `${maxWidth}px`;
			headerCell.style.minWidth = `${maxWidth}px`;
			headerCell.style.maxWidth = `${maxWidth}px`;

			dataCell.style.width = `${maxWidth}px`;
			dataCell.style.minWidth = `${maxWidth}px`;
			dataCell.style.maxWidth = `${maxWidth}px`;
		});
	}

	// Sets up a `ResizeObserver` to synchronize column widths when the table resizes.
	private initializeTableResizeObserver(): void {
		if (!this.table) {
			return;
		}
		this.resizeObserver = new ResizeObserver(() => {
			this.syncHeaderAndBodyColumnWidths();
		});
		this.resizeObserver.observe(this.table);
	}

	private isElementVisible(element: HTMLElement | null): boolean {
		if (!element) {
			return false;
		}
		return element.offsetParent !== null && getComputedStyle(element).display !== 'none';
	}

	// Checks if the table is initially visible and syncs column widths. If not, sets up a `MutationObserver` to detect visibility changes.
	private checkTableVisibility(): void {
		if (!this.tableContainer) {
			return;
		}

		if (this.isElementVisible(this.tableContainer)) {
			this.syncHeaderAndBodyColumnWidths();
			return;
		}

		this.mutationObserver = new MutationObserver(() => {
			if (this.isElementVisible(this.tableContainer)) {
				this.syncHeaderAndBodyColumnWidths();
				if (this.mutationObserver) {
					this.mutationObserver.disconnect();
					this.mutationObserver = null;
				}
			}
		});

		this.mutationObserver.observe(this.tableContainer, {attributes: true, attributeFilter: ['style', 'class']});
	}

	// Listen for vertical scroll on Parent Container/Window object
	private onVerticalScroll() {
		// If the table container or header row is not defined, exit the function early.
		if (!this.tableContainer || !this.headerRow) {
			return;
		}

		// Get the dimensions and position of the table container relative to the viewport.
		const containerRect = this.tableContainer.getBoundingClientRect();

		// If a parent container is defined, get its dimensions and position relative to the viewport.
		const parentContainerRect = this.parentContainer
			? this.parentContainer.getBoundingClientRect()
			: null;

		// Handle the sticky behavior for cases with a parent container (e.g., modal or specific scrollable area).
		if (parentContainerRect) {
			// Calculate the total scrollable height of the parent container.
			const scrollableContentHeight =
				this.parentContainer!.scrollHeight - this.parentContainer!.offsetHeight;

			// Check if the table container is within the visible area of the parent container
			// and if the parent container has enough scrollable content.
			if (
				containerRect.top < parentContainerRect.bottom &&
				containerRect.bottom > parentContainerRect.top &&
				scrollableContentHeight > 0 // Ensure the content is scrollable
			) {
				// Calculate the relative sticky top position for the header.
				const stickyTop = parentContainerRect.top - containerRect.top;

				// Apply the sticky behavior with the calculated top value.
				this.makeSticky(stickyTop);

				// Mark the header as sticky.
				this.isSticky = true;
			} else if (this.isSticky) {
				// If the table container is out of the visible area or not scrollable,
				// remove the sticky behavior if it was previously applied.
				this.removeSticky();
				this.isSticky = false;
			}
		} else {
			// Handle the sticky behavior for normal cases (e.g., window scrolling).
			if (containerRect.top <= 0 && containerRect.bottom > 0 && !this.isSticky) {
				// Apply sticky behavior when the table header is within the viewport.
				this.makeSticky(0, true); // Fixed sticky header at the top of the viewport.
				this.isSticky = true;
			} else if (
				// Remove sticky behavior when the table container is completely out of view.
				(containerRect.top >= 5 || containerRect.bottom <= 0) &&
				this.isSticky
			) {
				this.removeSticky();
				this.isSticky = false;
			}
		}
	}


	// Synchronizes horizontal scrolling between the header and table container.
	private onHorizontalScroll() {
		// Adjusts the horizontal scroll position of the header row to match the table container.
		if (this.headerRow && this.tableContainer) {
			requestAnimationFrame(() => {
				this.headerRow!.scrollTo(this.tableContainer!.scrollLeft, 0);
			});
		}
	}

	// apply sticky behavior on table header
	private makeSticky(stickyTop: number, addPlaceholder: boolean = false): void {
		if (!this.headerRow || !this.tableContainer) {
			return;
		}

		const containerRect = this.tableContainer.getBoundingClientRect();

		// Create placeholder row to avoid height change
		if (addPlaceholder) {
			this.addPlaceholderHeaderRow(this.headerRow);
		}

		// Apply sticky styles
		this.renderer.addClass(this.headerRow, this.styleClass);
		this.renderer.setStyle(this.headerRow, 'top', `${stickyTop}px`);
		this.renderer.setStyle(this.headerRow, 'left', `${containerRect.left}px`);
		this.renderer.setStyle(this.headerRow, 'width', `${containerRect.width}px`);
	}

	// remove sticky behavior from table header
	private removeSticky() {
		if (!this.headerRow) {
			return;
		}

		// Remove sticky styles
		this.renderer.removeClass(this.headerRow, this.styleClass);
		this.renderer.removeStyle(this.headerRow, 'left');
		this.renderer.removeStyle(this.headerRow, 'width');
		this.renderer.removeStyle(this.headerRow, 'top');
		this.renderer.removeStyle(this.headerRow, 'position');

		// Remove placeholder row
		this.removePlaceholderHeaderRow();
	}


	private addPlaceholderHeaderRow(headerRow: HTMLElement) {
		if (!this.placeholderHeaderRow) {
			this.placeholderHeaderRow = this.renderer.createElement('div');
			this.renderer.setStyle(this.placeholderHeaderRow, 'height', `${headerRow.offsetHeight}px`);
			this.renderer.setStyle(this.placeholderHeaderRow, 'width', '100%');
			this.renderer.setStyle(this.placeholderHeaderRow, 'visibility', 'hidden');
			this.renderer.addClass(this.placeholderHeaderRow, 'sticky-placeholder');

			this.renderer.insertBefore(
				this.tableContainer,
				this.placeholderHeaderRow,
				this.tableContainer?.firstChild
			);
		}
	}

	private removePlaceholderHeaderRow() {
		if (this.placeholderHeaderRow) {
			this.renderer.removeChild(this.tableContainer, this.placeholderHeaderRow);
			this.placeholderHeaderRow = null;
		}
	}
}
